import { SearchLand } from '@lt/api/dist/methods/search/types';
import { GroupName } from '@lt/api/dist/methods/references/types';
import { convertServerDateToClientDate, formatServerDate } from '../../../utils';
import { DatePickerState } from '../components/DatePicker';
import { SearchType } from '../types';
import { SEARCH_TYPE_NAMES, DEFAULT_DEPARTURE_COUNTRY_ISO2 } from '../constants';
import { getCornerDates } from '../components/DatePicker/components/Calendar/utils';
import { RequiredValues } from '../context';

export const buildLandParams = ({
  departure,
  destination,
  datepicker,
  tourists,
  host,
  searchType,
}: RequiredValues & {
  host?: string;
  searchType: SearchType;
}): SearchLand => ({
  ...destinationParams(destination),
  from_city: searchType === SEARCH_TYPE_NAMES.PACKAGE ? departure.value : 'any',
  from_country: departure
    ? departure.iso2 || DEFAULT_DEPARTURE_COUNTRY_ISO2
    : DEFAULT_DEPARTURE_COUNTRY_ISO2,
  adults: tourists.adults,
  kids: tourists.kids.length,
  kids_ages: tourists.kids.join(','),
  ...serverFlexDates(datepicker),
  host,
  search_type: searchType,
});

const destinationParams = ({ groupName, value, iso2 }: Destination): DestinationParams => {
  switch (groupName) {
    case 'hotels':
      return {
        hotel_ids: value,
        to_country: iso2,
      };
    case 'metaregions':
    case 'allMetaregions':
      return {
        to_metaregion: value,
      };
    case 'countries':
    case 'popularCountries':
    case 'allCountries':
      return {
        to_country: iso2,
      };
    default:
      return {
        to_city: value,
        to_country: iso2,
      };
  }
};

const serverFlexDates = ({
  left,
  right,
}: DatePickerState): {
  start_date_from: string;
  start_date_till: string;
  end_date_from: string;
  end_date_till: string;
  start_date_primary: string;
  end_date_primary: string;
} => {
  const startDates = getCornerDates(left);
  const endDates = getCornerDates(right);

  return {
    start_date_from: formatServerDate(startDates.from),
    start_date_till: formatServerDate(startDates.to),
    end_date_from: formatServerDate(endDates.from),
    end_date_till: formatServerDate(endDates.to),
    start_date_primary: convertServerDateToClientDate(left.date),
    end_date_primary: convertServerDateToClientDate(right.date),
  };
};

type Destination = {
  groupName: GroupName;
  value: number | string;
  iso2?: string;
};

type DestinationParams = Pick<SearchLand, 'hotel_ids' | 'to_country' | 'to_metaregion' | 'to_city'>;
